import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import girl from './images/girl.jpg'

// ─── Types ────────────────────────────────────────────────────────────────────

type TStudyStepsProps = {
  program: Awaited<ReturnType<typeof getProgramData>>
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LABEL = 'С момента поступления на курс до получения диплома'
const ADDITIONAL_INFO =
  'Вы можете самостоятельно распределять время обучения под ваш образ жизни и учиться с комфортом. В ходе программы также предусмотрены учебные блоки в записи, которые будет легко встроить в привычный ритм дня'

const DEFAULT_STEPS = [
  {
    name: 'Поступление дистанционно',
    description:
      'Дистанционная форма обучения на курсах и программах высшего образования предусматривает подачу документов удаленно. При желании можно приехать в офис.'
  },
  {
    name: 'Занятия в онлайн-формате',
    description:
      'Вебинары проходят на платформе университета. Если студенты не смогли присутствовать на лекциях, они смотрят записи занятий, которые сохраняются в личном кабинете до конца обучения.'
  },
  {
    name: 'Практика',
    description:
      'Студенты выполняют практические задания и отправляют свои проекты на проверку через систему дистанционного обучения.'
  },
  {
    name: 'Сдача экзаменов',
    description:
      'Приезжать в институт нет необходимости. Студенты сдают экзамены в режиме онлайн.'
  },
  {
    name: 'Дипломная работа',
    description:
      'В конце обучения студенты пишут и защищают выпускную работу при консультации научного руководителя.'
  },
  {
    name: 'Защита диплома',
    description:
      'Итоговая аттестация проходит на базе университета МИТМ. При желании можно пройти удаленно.'
  }
]

const STEP = 210
// Должно совпадать с duration transition (ms)
const TRANSITION_DURATION = 600

const IconScreen = ({ color }: { color: string }) => (
  <svg
    width='144'
    height='112'
    viewBox='0 0 144 112'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='shrink-0 w-[144px] h-[112px]'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M8 0C3.58172 0 0 3.58172 0 8V72C0 76.4183 3.58173 80 8 80H38.6106L31 112H74L72.0974 80H136C140.418 80 144 76.4183 144 72V8C144 3.58172 140.418 0 136 0H8Z'
      fill={color}
    />
  </svg>
)

const getBg = (pos: number, color: string) => {
  if (pos === 0) return color
  if (pos === 1) return 'rgb(230,230,230)'
  return '#E2E0E0'
}
const getLeft = (pos: number) => (pos >= 2 ? '9%' : '0%')
const getRight = (pos: number) => (pos >= 2 ? '9%' : '0%')
const getPadding = (pos: number) => (pos >= 2 ? '24px' : '32px')
const getTitleSize = (pos: number) => (pos >= 2 ? '16px' : '20px')
const getTitleColor = (pos: number) => (pos === 0 ? 'white' : pos === 1 ? '#000' : '#4F4F4F')
const getDescSize = (pos: number) => (pos === 0 ? '16px' : pos === 1 ? '14px' : '13px')
const getDescColor = (pos: number) =>
  pos === 0 ? 'rgba(255,255,255,0.85)' : pos === 1 ? '#555' : '#8B8B8B'

// ─── Component ────────────────────────────────────────────────────────────────

export const StudyStepsSection = ({ program }: TStudyStepsProps) => {

  const color = program?.faculty?.color ?? '#3A52EE'

  const steps =
    program?.studySteps && program.studySteps.length > 0
      ? program.studySteps
      : DEFAULT_STEPS

  const total = steps.length
  const [index, setIndex] = useState(0)

  // Множество карточек которые прямо сейчас анимируют уход вверх.
  // Пока карточка в этом сете — она рисуется на translateY(-STEP) с transition.
  // После завершения анимации — убирается из сета и мгновенно (без transition)
  // телепортируется вниз в очередь.
  const [exitingSet, setExitingSet] = useState<Set<number>>(new Set())
  const exitTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const timers = exitTimers.current
    const interval = setInterval(() => {
      setIndex(prev => {
        const exitingIdx = prev

        // 1. Добавляем в exiting — карточка начинает уезжать вверх с transition
        setExitingSet(s => new Set(s).add(exitingIdx))

        // 2. После окончания transition — убираем из exiting.
        //    React перерисует без transition → карточка мгновенно окажется внизу
        const timer = setTimeout(() => {
          setExitingSet(s => {
            const next = new Set(s)
            next.delete(exitingIdx)
            return next
          })
          timers.delete(exitingIdx)
        }, TRANSITION_DURATION + 50) // небольшой запас

        timers.set(exitingIdx, timer)

        return (prev + 1) % total
      })
    }, 3000)

    return () => {
      clearInterval(interval)
      timers.forEach(t => clearTimeout(t))
    }
  }, [total])

  const getPos = (i: number) => ((i - index) % total + total) % total

  const getTranslateY = (i: number, pos: number): number => {
    // Карточка уходит вверх — анимированно
    if (exitingSet.has(i)) return -STEP
    // Карточки вне видимой зоны (pos >= 3) — ждут внизу без анимации
    if (pos >= 3) return pos * STEP
    return pos * STEP
  }

  const hasTransition = (i: number, pos: number): boolean => {
    // Уходящая — transition есть (анимируем уход вверх)
    if (exitingSet.has(i)) return true
    // Карточки в очереди внизу — без transition (телепортируются)
    if (pos >= 3) return false
    // Видимые (0,1,2) — с transition
    return true
  }

  return (
    <section
      className='py-[56px] 2lg:py-[80px] bg-[#F2F2F2]'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        {/* Header */}
        <div className='flex flex-col items-start mb-[32px] 2lg:mb-[48px]'>
          <span
            className='inline-block mb-[16px] px-[16px] py-[8px] rounded-[55px] border text-[12px] 2lg:text-[14px] font-medium leading-[120%] border-black'>
            {LABEL}
          </span>
          <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-medium leading-[120%]'>
           Порядок обучения
          </h2>
        </div>

        {/* Content */}
        <div className='grid grid-cols-1 2lg:grid-cols-[1fr_2fr] gap-[32px] md:gap-[40px]'>
          <div className='relative overflow-hidden' style={{ minHeight: '600px' }}>
            <div
              className='absolute left-1/2 -translate-x-1/2 w-[2px] h-full z-[1] pointer-events-none'
              style={{ backgroundColor: color }}
            />

            {steps.map((step, i) => {
              const pos = getPos(i)
              const translateY = getTranslateY(i, pos)
              const withTransition = hasTransition(i, pos)
              const zIndex = total - pos + 5

              return (
                <div
                  key={i}
                  className='absolute rounded-[16px] box-border'
                  style={{
                    top: 0,
                    left: getLeft(pos),
                    right: getRight(pos),
                    zIndex,
                    padding: getPadding(pos),
                    backgroundColor: getBg(pos, color),
                    transform: `translateY(${translateY}px)`,
                    transition: withTransition
                      ? `transform ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), background-color 0.6s ease, left 0.6s ease, right 0.6s ease, padding 0.4s ease`
                      : 'none'
                  }}>
                  <p
                    className='font-medium leading-[120%] mb-[12px] mt-0'
                    style={{
                      fontSize: getTitleSize(pos),
                      color: getTitleColor(pos),
                      transition: withTransition ? 'color 0.4s ease' : 'none'
                    }}>
                    {step?.name ?? ''}
                  </p>
                  <p
                    className='leading-[140%] m-0'
                    style={{
                      fontSize: getDescSize(pos),
                      color: getDescColor(pos),
                      transition: withTransition ? 'color 0.4s ease' : 'none'
                    }}>
                    {step?.description ?? ''}
                  </p>
                </div>
              )
            })}
          </div>

          {/* Right: girl image */}
          <div className='relative min-h-[248px] md:min-h-[464px] 2lg:min-h-[506px] rounded-[16px] overflow-hidden'>
            <Image
              src={girl}
              alt='girl'
              fill
              quality={75}
              sizes='(max-width: 768px) 75vw, 50vw'
              className='object-cover rounded-[16px]'
            />
          </div>
        </div>

        {['kursy'].includes(program?.category?.slug ?? '') && (
          <div className='flex flex-col md:flex-row items-center gap-[16px] 2lg:gap-[32px] mt-[57px] p-[32px] rounded-[16px] bg-white'>
            <IconScreen color='#fb574b' />
            <p className='text-[16px] 2lg:text-[20px] leading-[140%]'>
              {ADDITIONAL_INFO}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}